import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import { ApiQueryName } from "@/models/api"
import { useRenameQueryResult } from "@/services/utils"
import type { SuccessData } from "@/utils/api"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"
import type { AxiosError, AxiosResponse } from "axios"

import { createQueryKey } from "../utils/createQueryKey"
import { useInvalidateQueries } from "../utils/useInvalidateQueries"

export interface UserMutationError extends SuccessData {
  errors: string[]
}

export const useUserMutation = <
  TInput,
  TResponse extends SuccessData,
  TPrefix extends string,
>({
  mutationFn,
  resultKey,
  fallbackErrorMessage,
  options = {},
}: {
  mutationFn: (input: TInput) => Promise<AxiosResponse<TResponse>>
  resultKey: TPrefix
  fallbackErrorMessage: string
  options?: UseMutationOptions<
    AxiosResponse<TResponse>,
    AxiosError<UserMutationError>,
    TInput
  >
}) => {
  const { invalidateQueries } = useInvalidateQueries()
  const { setNotification } = useNotification()
  const { t } = useTranslation("common")

  const mutation = useMutation({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn,
    onError: (error, variables, onMutateResult, context) => {
      const errorMessage = getErrorNotificationMessage(
        error,
        error.response?.data?.errors?.[0] ?? t(fallbackErrorMessage)
      )

      setNotification({
        message: errorMessage,
        type: "error",
      })

      options?.onError?.(error, variables, onMutateResult, context)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateQueries([
        createQueryKey(ApiQueryName.User, "getOne"),
        createQueryKey(ApiQueryName.Users, "getMany"),
      ])

      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })

  return useRenameQueryResult(mutation, resultKey)
}
